import { ApolloError, useQuery } from "@apollo/client";
import { useMemo, useRef } from "react";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo";
import TRANSACTION_QUERY from "../apollo/transactions.gql";
import { IComparableArgsGQL, RecordType } from "../typings";

export interface TransactionsQueryData {
  edges?: {
    node: RecordType;
  }[];
}

type TransactionsHookArgs = Omit<IComparableArgsGQL, "highlight">;
interface TransactionsHookResponse extends Omit<IComparableArgsGQL, "highlight"> {
  loading: boolean;
  error: ApolloError | undefined;
  data: RecordType[];
}

export function useTransactionQuery({
  search,
  sort,
}: TransactionsHookArgs): TransactionsHookResponse {
  const { loading, error, data } = useQuery<{ comparableSearch: TransactionsQueryData }>(
    TRANSACTION_QUERY,
    {
      variables: {
        search,
        sort,
      },
      client: routedClient,
      context: {
        endpoint: LandTechEndpoints.Gateway,
      },
      // transactions queries are invalid without at least one of these location based searches
      skip:
        !search.bounding_box_geo_filter?.bottom_right &&
        !search.poly_geo_filter?.coordinates &&
        !search.radius_geo_filter?.location,
    }
  );

  const lastResult = useRef(data);

  if (data !== lastResult.current) {
    lastResult.current = data;
  }
  return useMemo(
    () => ({
      loading,
      error,
      data: !error ? data?.comparableSearch.edges?.map((record) => record.node) || [] : [],
      search,
      sort,
    }),
    [lastResult.current]
  );
}
